import CaseRecords from "@/components/admin/CaseRecords";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Eye, Globe2, FileText, Download, Truck, Loader2, CheckCircle2, Circle, StickyNote, Mail, AlertTriangle, Ban, Trash2, Pencil, ExternalLink, ShieldCheck } from "lucide-react";
import { api, errMsg, downloadFile, fmtDate, fmtDateTime } from "@/lib/api";
import { Card, Empty, ConfirmDialog } from "@/components/admin/ui";
import { StatePackageButton } from "@/components/admin/StatePackage";
import { StatusBadge } from "@/components/StatusBadge";
import { StateAuthBadge } from "@/components/admin/StateAuthBadge";
import AddSearchForm from "@/components/admin/AddSearchForm";
import CaseAttachments from "@/components/admin/CaseAttachments";
import { SetParish } from "@/components/admin/SetParish";
import { NameFlags } from "@/components/admin/NameFlags";
import { InfoRequest } from "@/components/admin/InfoRequest";
import { BillingSummary } from "@/components/admin/BillingSummary";
import { ExtraFees } from "@/components/admin/ExtraFees";
import { InternalFiles } from "@/components/admin/InternalFiles";
import { TabBar, useSavedTab, Tab } from "@/components/admin/TabBar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ChevronDown, MoreHorizontal } from "lucide-react";

function Section({ title, open: init = false, alert, testId, children }: { title: string; open?: boolean; alert?: boolean; testId: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(init);
  return (
    <div className="border-t border-slate-100 first:border-t-0">
      <button type="button" onClick={() => setOpen(!open)} data-testid={testId} aria-expanded={open}
        className="flex w-full items-center justify-between px-6 py-3 text-left text-[13px] font-semibold uppercase tracking-wide text-[#64748B] transition-colors hover:text-[#1B365D]">
        <span className="flex items-center gap-2">{title}{alert && <span className="h-2 w-2 rounded-full bg-[#B91C1C]" />}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-6 pb-4">{children}</div>}
    </div>
  );
}

const MANUAL = [["new", "New"], ["in_progress", "In progress"], ["awaiting_records", "Awaiting records"], ["on_hold", "On hold"], ["review", "In review"]];
const RESULTS = [["", "Choose result…"], ["clear", "No Record Found"], ["record_found", "Record(s) Found – See Attached"], ["unable_to_verify", "Unable to Verify"], ["not_applicable", "Not Applicable"]];

const TONE: Record<string, [string, string]> = {
  record_found: ["border-[#B91C1C]/30 bg-[#FEF2F2]", "text-[#B91C1C]"],
  unable_to_verify: ["border-[#D97706]/35 bg-[#FFFBEB]", "text-[#B45309]"],
};
const GREEN: [string, string] = ["border-[#15803D]/25 bg-[#F0FDF4]", "text-[#15803D]"];

function ScopeSwitch({ s, locked, onUpdate }: { s: any; locked: boolean; onUpdate: (id: string, body: any) => Promise<void> }) {
  const scope = s.scope || "statewide";
  const [counties, setCounties] = useState(s.counties || "");
  useEffect(() => setCounties(s.counties || ""), [s.counties]);
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`search-scope-${s.id}`}>
      <div className="inline-flex rounded-lg bg-[#F1EFEA] p-0.5">
        {[["statewide", "Statewide"], ["county", "County"]].map(([v, l]) => (
          <button key={v} type="button" disabled={locked} onClick={() => v !== scope && onUpdate(s.id, { scope: v, counties })} data-testid={`search-scope-${v}-${s.id}`}
            className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${scope === v ? "bg-white text-[#1B365D] shadow-sm" : "text-[#64748B]"}`}>{l}</button>
        ))}
      </div>
      {scope === "county" && (
        <input disabled={locked} className="field-input h-8 w-60 text-xs" placeholder="Counties, e.g. Chester, Delaware" value={counties} data-testid={`search-counties-input-${s.id}`}
          onChange={(e) => setCounties(e.target.value)} onBlur={() => counties !== (s.counties || "") && onUpdate(s.id, { scope: "county", counties })} />
      )}
    </div>
  );
}

function SearchRow({ s, locked, onUpdate, onRemove }: { s: any; locked: boolean; onUpdate: (id: string, body: any) => Promise<void>; onRemove: (id: string) => Promise<void> }) {
  const tone = s.completed ? TONE[s.result] || GREEN : null;
  const [confirmRm, setConfirmRm] = useState(false);
  const [notes, setNotes] = useState(s.notes || "");
  const [desc, setDesc] = useState(s.description || "");
  const [showDesc, setShowDesc] = useState(false);
  useEffect(() => setNotes(s.notes || ""), [s.notes]);
  useEffect(() => setDesc(s.description || ""), [s.description]);
  return (
    <div className={`rounded-xl border-2 p-4 transition-colors ${tone ? tone[0] : "border-slate-200 bg-white"}`} data-testid={`search-item-${s.id}`} data-tone={s.completed ? s.result || "done" : "open"}>
      <div className="flex flex-wrap items-start gap-3">
        <button
          disabled={locked}
          onClick={() => onUpdate(s.id, { completed: !s.completed })}
          data-testid={`search-complete-toggle-${s.id}`}
          className="mt-0.5 shrink-0 disabled:opacity-50"
          aria-label="Toggle complete"
        >
          {tone ? <CheckCircle2 className={`h-6 w-6 ${tone[1]}`} /> : <Circle className="h-6 w-6 text-slate-300 hover:text-[#1B365D]" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="font-semibold text-[#1A2536]">{s.type}{s.type.startsWith("Foreign") && <Globe2 className="ml-1.5 inline h-4 w-4 text-[#D97706]" />}</p>
            {!locked && (confirmRm ? (
              <span className="flex shrink-0 items-center gap-2 text-xs font-semibold">
                <button type="button" className="text-[#B91C1C] hover:underline" onClick={() => onRemove(s.id)} data-testid={`search-remove-confirm-${s.id}`}>Remove from report</button>
                <button type="button" className="text-[#64748B] hover:underline" onClick={() => setConfirmRm(false)} data-testid={`search-remove-cancel-${s.id}`}>Keep</button>
              </span>
            ) : (
              <button type="button" className="shrink-0 text-[#94A3B8] hover:text-[#B91C1C]" title="Remove this search (not applicable)" onClick={() => setConfirmRm(true)} data-testid={`search-remove-${s.id}`}><Trash2 className="h-4 w-4" /></button>
            ))}
          </div>
          <p className="text-sm text-[#64748B]">{s.jurisdiction}</p>
          {s.template === "state_courts" && <ScopeSwitch s={s} locked={locked} onUpdate={onUpdate} />}
          <button type="button" className="mt-1 text-xs font-semibold text-[#1B365D] hover:underline" onClick={() => setShowDesc(!showDesc)} data-testid={`search-desc-toggle-${s.id}`}>
            {showDesc ? "Hide report scope text" : s.description ? "Edit report scope text" : "Add report scope text"}
          </button>
          {s.completed && <p className={`mt-1 text-xs ${tone![1]}`}>Completed by {s.completed_by} · {fmtDateTime(s.completed_at)}</p>}
        </div>
        <select
          disabled={locked}
          className="field-input h-10 w-44 text-sm"
          value={s.result || ""}
          data-testid={`search-result-select-${s.id}`}
          onChange={(e) => e.target.value && onUpdate(s.id, { result: e.target.value, ...(s.completed ? {} : { completed: true }) })}
        >
          {RESULTS.map(([v, l]) => <option key={v} value={v} disabled={!v}>{l}</option>)}
        </select>
      </div>
      <input
        disabled={locked}
        className="field-input mt-3 h-10 text-sm"
        placeholder="Source / notes (e.g. court name, record details)"
        value={notes}
        data-testid={`search-notes-input-${s.id}`}
        onChange={(e) => setNotes(e.target.value)}
        onBlur={() => notes !== (s.notes || "") && onUpdate(s.id, { notes })}
      />
      {showDesc && (
        <textarea
          disabled={locked}
          rows={4}
          className="field-input mt-2 h-auto py-2 text-sm"
          placeholder="Scope description printed on the report"
          value={desc}
          data-testid={`search-description-input-${s.id}`}
          onChange={(e) => setDesc(e.target.value)}
          onBlur={() => desc !== (s.description || "") && onUpdate(s.id, { description: desc })}
        />
      )}
    </div>
  );
}

function Info({ k, v, testId }: { k: string; v?: React.ReactNode; testId?: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-2.5 text-sm last:border-0">
      <span className="text-[#64748B]">{k}</span>
      <span className="text-right font-medium text-[#1A2536]" data-testid={testId}>{v || "—"}</span>
    </div>
  );
}

function EmailReport({ c, busy, send }: { c: any; busy: boolean; send: (b: { org_emails: string[]; dow_emails: string[]; to_applicant: boolean }) => void }) {
  const appEmail = c.data?.personal?.email || "";
  const rec = c.report_recipients || { org_named: [], dow: [] };
  const [pick, setPick] = useState<string[]>(() => (!rec.is_dow && rec.org_named[0] ? [rec.org_named[0].email] : []));
  const [toApp, setToApp] = useState(!!appEmail);
  const flip = (e: string) => setPick((p) => (p.includes(e) ? p.filter((x) => x !== e) : [...p, e]));
  const row = (email: string, label: string, tid: string, on: boolean, change: () => void) => (
    <label key={email} className="flex cursor-pointer items-center gap-2 text-sm">
      <input type="checkbox" className="h-4 w-4 accent-[#1B365D]" checked={on} onChange={change} data-testid={tid} />
      <span>{label && <b>{label} </b>}<span className="text-[#64748B]">{email}</span></span>
    </label>
  );
  const orgPick = pick.filter((e) => rec.org_named.some((o: any) => o.email === e));
  const dowPick = pick.filter((e) => rec.dow.includes(e));
  return (
    <div className="w-full space-y-2 rounded-xl border border-slate-200 p-3" data-testid="case-email-report-panel">
      {!rec.is_dow && <p className="text-xs font-bold uppercase tracking-wide text-[#64748B]">Organization contacts</p>}
      {!rec.is_dow && (rec.org_named.length ? rec.org_named.map((o: any, i: number) => row(o.email, o.name, `case-email-report-org-${i}`, pick.includes(o.email), () => flip(o.email)))
        : <p className="text-sm text-[#64748B]">No contact emails on file.</p>)}
      {rec.is_dow && rec.dow.length > 0 && <p className="pt-1 text-xs font-bold uppercase tracking-wide text-[#64748B]">Also send to the Diocese (record found or unable to verify)</p>}
      {rec.is_dow && rec.dow.map((e: string, i: number) => row(e, "", `case-email-report-dow-${i}`, pick.includes(e), () => flip(e)))}
      {appEmail && <p className="pt-1 text-xs font-bold uppercase tracking-wide text-[#64748B]">Applicant</p>}
      {appEmail && row(appEmail, "", "case-email-report-applicant-checkbox", toApp, () => setToApp(!toApp))}
      <p className="text-xs text-[#64748B]">Each person gets their own secure link and a one-time code at their address.</p>
      <button className="btn-primary btn-sm" disabled={busy || !(pick.length || toApp)} data-testid="case-email-report-to-org-button" onClick={() => send({ org_emails: orgPick, dow_emails: dowPick, to_applicant: toApp })}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} {rec.is_dow ? (c.report_email ? "Resend report" : "Send report") : c.report_email ? "Resend report link" : "Email secure report link"}
      </button>
    </div>
  );
}

function BillingOmit({ c, onSaved }: { c: any; onSaved: (u: any) => void }) {
  const [reason, setReason] = useState(c.billing_omit_reason || "");
  const save = (omit: boolean) => api.patch(`/admin/cases/${c.id}/billing-omit`, { omit, reason }).then((r) => { onSaved(r.data); toast.success(omit ? "Omitted from billing" : "Back on billing"); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className={`mt-3 rounded-xl border p-3 ${c.billing_omit ? "border-[#D97706]/40 bg-[#FFFBEB]" : "border-slate-200"}`} data-testid="case-billing-omit">
      {c.billing_omit ? (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="font-semibold text-[#9A5B00]" data-testid="case-billing-omit-status">Omitted from billing{c.billing_omit_reason ? `: ${c.billing_omit_reason}` : ""}</span>
          <button type="button" className="btn-secondary btn-sm" onClick={() => save(false)} data-testid="case-billing-restore-button">Put back on billing</button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <input className="field-input h-9 w-full min-w-0 text-sm" placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="case-billing-omit-reason" />
          <button type="button" className="btn-secondary btn-sm justify-center" onClick={() => save(true)} data-testid="case-billing-omit-button"><Ban className="h-4 w-4" /> Omit from billing</button>
        </div>
      )}
    </div>
  );
}

function DeleteCase({ c, onDeleted }: { c: any; onDeleted: () => void }) {
  const [open, setOpen] = useState(false);
  const del = async () => { try { await api.delete(`/admin/cases/${c.id}`); toast.success("Applicant deleted"); onDeleted(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <>
      <button type="button" className="btn-secondary btn-sm whitespace-nowrap text-[#B91C1C]" onClick={() => setOpen(true)} data-testid="case-delete-button"><Trash2 className="h-4 w-4" /> Delete<span className="hidden sm:inline"> applicant</span></button>
      <ConfirmDialog open={open} onOpenChange={setOpen} testId="case-delete-dialog" title="Delete this applicant permanently?"
        description="The application, its searches, records and report will be removed and cannot be recovered. Any report links sent will stop working. The deletion is recorded in the audit log."
        confirmLabel="Delete applicant" onConfirm={del} />
    </>
  );
}

export default function CaseDetail() {
  const { id } = useParams();
  const [c, setC] = useState<any>(null);
  const [pii, setPii] = useState<any>(null);
  const [note, setNote] = useState("");
  const [tab, setTab] = useSavedTab("case-tab", "searches", ["searches", "report", "requests", "notes", "activity"]);
  const [busy, setBusy] = useState("");
  const [confirmDeliver, setConfirmDeliver] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const navigate = useNavigate();
  const { user, can } = useAuth() as any;
  const load = useCallback(() => api.get(`/admin/cases/${id}`).then((r) => setC(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  if (!c) return <div className="h-40 animate-pulse rounded-2xl bg-white" />;

  const p = c.data?.personal || {};
  const name = [p.first_name, p.middle_name, p.last_name].filter(Boolean).join(" ") || "(not entered yet)";
  const submitted = c.status === "submitted";
  const delivered = c.case_status === "delivered";
  const required = (c.searches || []).filter((s: any) => s.required);
  const done = required.filter((s: any) => s.completed).length;
  const allDone = required.length > 0 && done === required.length;
  const remaining = required.length - done;
  const perms = c.permissions || {};

  const act = async (key: string, fn: () => Promise<any>, ok?: string) => {
    setBusy(key);
    try {
      await fn();
      if (ok) toast.success(ok);
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy("");
  };
  const removeSearch = async (sid: string) => {
    try {
      const { data } = await api.delete(`/admin/cases/${id}/searches/${sid}`);
      setC((prev: any) => ({ ...prev, ...data, timeline: prev.timeline, permissions: prev.permissions }));
      toast.success("Search removed");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const updateSearch = async (sid: string, body: any) => {
    try {
      const { data } = await api.patch(`/admin/cases/${id}/searches/${sid}`, body);
      setC((prev: any) => ({ ...prev, ...data, timeline: prev.timeline, permissions: prev.permissions }));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <Link to="/admin/cases" className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-semibold text-[#64748B] hover:text-[#1B365D]"><ArrowLeft className="h-4 w-4" /> Case queue</Link>
        <div className="flex items-center gap-2">
          {user?.role === "admin" && <Link to={`/admin/cases/${id}/edit`} className="btn-secondary btn-sm whitespace-nowrap" data-testid="case-edit-button"><Pencil className="h-4 w-4" /> Edit<span className="hidden sm:inline"> applicant</span></Link>}
          {c.submitted_at && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild><button className="btn-secondary btn-sm" data-testid="case-more-menu" aria-label="More actions"><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {c.airtable_url && <DropdownMenuItem onClick={() => window.open(c.airtable_url, "_blank", "noopener")} data-testid="case-airtable-link"><ExternalLink className="mr-2 h-4 w-4" /> View in Airtable ({c.airtable_table})</DropdownMenuItem>}
                <DropdownMenuItem data-testid="case-airtable-resend" onClick={() => api.post(`/admin/cases/${id}/airtable-resend`).then((r) => { toast.success(`Sent to Airtable ${r.data.table}${r.data.leftover ? `. The old ${r.data.leftover.table} record couldn't be removed (Airtable access), so please delete it there by hand` : r.data.replaced && r.data.replaced !== r.data.table ? ` (removed from ${r.data.replaced})` : ""}`, { duration: r.data.leftover ? 12000 : 4000 }); load(); }).catch((e) => toast.error(errMsg(e)))}>
                  <ExternalLink className="mr-2 h-4 w-4" /> {c.airtable_url ? "Resend to Airtable" : "Send to Airtable"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {can("delete_applicants") && <DeleteCase c={c} onDeleted={() => navigate("/admin/cases")} />}
        </div>
      </div>
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-extrabold tracking-tight" data-testid="case-applicant-name">{name}</h1>
            <StatusBadge status={submitted ? c.case_status : c.status} testId="case-status-badge" />
            {submitted && <StateAuthBadge c={c} onChanged={load} />}
          </div>
          <p className="mt-1.5 text-[15px] text-[#64748B]">
            <Link to={`/admin/organizations/${c.org_id}`} className="font-semibold text-[#1B365D] hover:underline">{c.org_name}</Link> · {c.code ? <>Code <span className="font-mono">{c.code}</span></> : <span className="font-semibold text-[#B45309]" data-testid="case-manual-badge">Manual entry ({c.received_via})</span>} · Submitted {fmtDate(c.submitted_at)}
          </p>
        </div>
        {submitted && !delivered && (
          <label className="flex items-center gap-3">
            <span className="text-sm font-semibold text-[#475569]">Case status</span>
            <select
              className="field-input h-11 w-52"
              data-testid="admin-case-status-select"
              value={MANUAL.some(([v]) => v === c.case_status) ? c.case_status : ""}
              onChange={(e) => act("status", () => api.patch(`/admin/cases/${id}/status`, { status: e.target.value }), "Status updated")}
            >
              {!MANUAL.some(([v]) => v === c.case_status) && <option value="">Report ready</option>}
              {MANUAL.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
        )}
      </div>

      {c.flags?.foreign_address && (
        <div className="mb-6 flex items-center gap-3 rounded-2xl border border-[#D97706]/30 bg-[#FEF3C7] px-5 py-4 text-[15px] text-[#92400E]" data-testid="case-foreign-flag">
          <Globe2 className="h-5 w-5 shrink-0" /> This applicant listed an address outside the United States. A manual foreign-address review is required.
        </div>
      )}
      {submitted && (c.airtable_held || c.data?.dow || c.dow_match) && <SetParish c={c} onDone={load} />}
      {!submitted && <div className="mb-6 rounded-2xl bg-[#F1EFEA] px-5 py-4 text-[15px] text-[#475569]">This application is {c.status === "locked" ? "locked because it wasn't finished within 24 hours" : `in progress (step ${(c.current_step || 0) + 1} of 4)`}.</div>}

      <div className="grid gap-6 xl:grid-cols-[380px_1fr]">
        <div className="space-y-6">
          <Card title="Applicant" testId="case-applicant-card" actions={perms.reveal_pii && submitted && !pii && (
            <button className="btn-secondary btn-sm" data-testid="case-reveal-pii-button" onClick={() => act("pii", async () => setPii((await api.post(`/admin/cases/${id}/reveal-pii`)).data))}><Eye className="h-4 w-4" /> Reveal</button>
          )}>
            <Section title="Identity & contact" open testId="case-section-identity">
              <NameFlags p={p} />
              <Info k="Date of birth" v={p.dob} />
              <Info k="SSN" v={pii?.ssn || p.ssn} testId="case-ssn" />
              <Info k="Driver's license" v={(pii?.dl_number || p.dl_number) && `${pii?.dl_number || p.dl_number} ${p.dl_state || ""}`} />
              <Info k="Email" v={p.email} />
              <Info k="Phone" v={p.phone} />
            </Section>
            <Section title="Names & personal" testId="case-section-names">
              {p.name_changed && <Info k="Last name at birth" v={p.birth_same === "yes" ? "Same as current" : p.birth_last_name} testId="case-birth-last-name" />}
              <Info k="Other names" v={p.other_names} />
              <Info k="Gender" v={p.gender} />
              <Info k={p.name_changed ? "Marriages" : "Spouse(s)"} v={p.spouse} />
              <Info k="Employment" v={[p.employment_status, p.employer].filter(Boolean).join(" · ")} />
              {c.data?.dow && <Info k="Maiden name/marriage dates entered" v={c.data.dow.maiden_confirm} />}
            </Section>
            <Section title="Background answers" open={[c.data?.consent?.arrests, c.data?.consent?.convictions, c.data?.dow?.suspended, c.data?.dow?.revoked, c.data?.dow?.dismissal].includes("yes")}
              alert={[c.data?.consent?.arrests, c.data?.consent?.convictions, c.data?.dow?.suspended, c.data?.dow?.revoked, c.data?.dow?.dismissal].includes("yes")} testId="case-section-background">
              <Info k="Arrests (7 yrs)" v={c.data?.consent?.arrests && <span className={c.data.consent.arrests === "yes" ? "text-[#B91C1C]" : ""}>{c.data.consent.arrests}</span>} testId="case-arrests" />
              <Info k="Convictions (ever)" v={c.data?.consent?.convictions && <span className={c.data.consent.convictions === "yes" ? "text-[#B91C1C]" : ""}>{c.data.consent.convictions}</span>} testId="case-convictions" />
              {c.data?.consent?.criminal_explanation && <Info k="Explanation" v={c.data.consent.criminal_explanation} />}
              {c.data?.dow && (<>
                <Info k="License suspended/revoked" v={[c.data.dow.suspended, c.data.dow.revoked].filter(Boolean).join(" / ")} />
                {c.data.dow.driving_explanation && <Info k="Driving explanation" v={c.data.dow.driving_explanation} />}
                <Info k="Youth dismissal" v={c.data.dow.dismissal} />
                {c.data.dow.dismissal_explanation && <Info k="Dismissal explanation" v={c.data.dow.dismissal_explanation} />}
              </>)}
            </Section>
            {c.data?.dow?.location && (
              <Section title="Parish & role" testId="case-section-dow">
                <Info k="Organization" v={c.data.dow.location} testId="case-dow-location" />
                <Info k="Volunteer/Employee" v={[c.data.dow.role, c.data.dow.position].filter(Boolean).join(" · ")} />
                <Info k="Activities" v={(c.data.dow.activities || []).join(", ")} />
              </Section>
            )}
            <Section title="Signatures" testId="case-section-signatures">
              {c.data?.dow?.disclosure_signature && <Info k="Disclosure signed as" v={c.data.dow.disclosure_signature} />}
              {c.data?.dow?.privacy_signature && <Info k="Privacy notice signed as" v={c.data.dow.privacy_signature} />}
              <Info k="Signed as" v={c.data?.consent?.signature_name} />
              {c.data?.consent?.signature_image && <img src={c.data.consent.signature_image} alt="Signature" className="mt-2 h-16 rounded-lg border border-slate-100 bg-[#FCFBF8]" />}
            </Section>
            <Section title="Billing & reference" testId="case-section-billing">
              <label className="block"><span className="text-xs font-semibold text-[#64748B]">Billing note</span>
                <input className="field-input mt-1 h-10 text-sm" defaultValue={c.billing_note || ""} data-testid="case-billing-note-input"
                  onBlur={(e) => e.target.value !== (c.billing_note || "") && api.patch(`/admin/cases/${id}/billing-note`, { billing_note: e.target.value }).then(() => { toast.success("Billing note saved"); setC({ ...c, billing_note: e.target.value }); }).catch((er) => toast.error(errMsg(er)))} />
              </label>
              <label className="mt-3 block"><span className="text-xs font-semibold text-[#64748B]">Client / matter reference <span className="font-normal">(prints on the report)</span></span>
                <input className="field-input mt-1 h-10 text-sm" maxLength={200} placeholder="e.g. Smith v. Jones / File 24-118" defaultValue={c.client_matter || ""} data-testid="case-client-matter-input"
                  onBlur={(e) => e.target.value.trim() !== (c.client_matter || "") && api.patch(`/admin/cases/${id}/client-matter`, { client_matter: e.target.value }).then(() => { toast.success("Client / matter reference saved"); setC({ ...c, client_matter: e.target.value.trim() }); }).catch((er) => toast.error(errMsg(er)))} />
              </label>
              {perms.billing && <BillingOmit c={c} onSaved={(u) => setC({ ...c, ...u })} />}
            </Section>
          </Card>
          <Card title={`Address history (${(c.data?.addresses || []).length})`} testId="case-addresses-card">
            <ul className="divide-y divide-slate-100">
              {(c.data?.addresses || []).map((a: any, i: number) => (
                <li key={i} className="px-6 py-3 text-sm">
                  <p className="font-medium text-[#1A2536]">{a.street}, {a.city} {a.zip}</p>
                  <p className="text-[#64748B]">
                    {a.country === "United States" ? `${a.county ? a.county + " County, " : ""}${a.state}` : <span className="font-semibold text-[#B45309]">{a.country === "Other" ? a.other_country : a.country}</span>} · {a.from} → {a.to || "present"}
                  </p>
                </li>
              ))}
            </ul>
            {!(c.data?.addresses || []).length && <Empty text="No addresses yet." />}
          </Card>
        </div>

        <div className="min-w-0 space-y-6">
          <TabBar testId="case-tabs" prefix="case-tab" value={submitted ? tab : ["notes", "activity"].includes(tab) ? tab : "searches"} onChange={setTab} tabs={([
            ["searches", "Searches", remaining || undefined, "bg-[#FEF3C7] text-[#B45309]", remaining ? `${remaining} search(es) still to complete` : undefined],
            submitted && ["report", "Report & documents", undefined, ""],
            submitted && ["requests", "Requests & files", undefined, ""],
            ["notes", "Notes & fees", (c.notes || []).length || undefined, ""],
            ["activity", "Activity", undefined, ""],
          ].filter(Boolean)) as Tab[]} />
          {(tab === "searches" || (!submitted && !["notes", "activity"].includes(tab))) && <>
          <Card
            title="Required search checklist"
            testId="case-search-checklist"
            actions={<span className="font-mono text-sm font-semibold text-[#1B365D]" data-testid="case-search-progress">{done}/{required.length} complete</span>}
          >
            <div className="space-y-3 p-5">
              {submitted && !delivered && remaining > 0 && (
                <button type="button" className="btn-secondary btn-sm w-full justify-center" disabled={busy === "clear"} onClick={() => setConfirmClear(true)} data-testid="case-mark-remaining-clear-button">
                  {busy === "clear" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4 text-[#15803D]" />} Mark all {remaining} remaining search{remaining === 1 ? "" : "es"} No Record Found
                </button>
              )}
              {(c.searches || []).map((s: any) => <SearchRow key={s.id} s={s} locked={delivered} onUpdate={updateSearch} onRemove={removeSearch} />)}
              {!submitted && <Empty text="Searches are created when the application is submitted." />}
              {submitted && !delivered && (
                <AddSearchForm addresses={c.data?.addresses || []} existing={(c.searches || []).map((s: any) => s.type || "")} onAdd={(body) => act("add", () => api.post(`/admin/cases/${id}/searches`, body), "Search added")} />
              )}
            </div>
          </Card>

          {submitted && <CaseRecords c={c} locked={delivered} onCase={(data) => setC((prev: any) => ({ ...prev, ...data, timeline: prev.timeline, permissions: prev.permissions }))} />}
          </>}

          {tab === "requests" && submitted && <>
          <InfoRequest c={c} locked={delivered} onDone={load} />
          <CaseAttachments c={c} locked={delivered} onChange={(data) => setC((prev: any) => ({ ...prev, ...data, timeline: prev.timeline, permissions: prev.permissions }))} />
          </>}

          {tab === "report" && submitted && (
            <Card title="Final report" testId="case-report-card">
              <div className="flex flex-wrap items-center gap-3 p-6">
                {perms.generate_reports && !delivered && (
                  <button className="btn-primary btn-sm" disabled={!allDone || busy === "report"} data-testid="case-generate-report-button" onClick={() => act("report", () => api.post(`/admin/cases/${id}/report`), "Report generated")}>
                    {busy === "report" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} {c.report ? "Regenerate report" : "Generate final report"}
                  </button>
                )}
                {c.report && (
                  <button className="btn-secondary btn-sm" data-testid="admin-download-final-pdf-report-button" onClick={() => downloadFile(`/admin/cases/${id}/report.pdf`, `report-${name}.pdf`).catch((e) => toast.error(errMsg(e)))}>
                    <Download className="h-4 w-4" /> Download PDF (v{c.report.version})
                  </button>
                )}
                {perms.generate_reports && c.case_status === "report_ready" && (
                  <button className="btn-primary btn-sm bg-[#15803D] hover:bg-[#166534]" data-testid="case-mark-delivered-button" onClick={() => setConfirmDeliver(true)}><Truck className="h-4 w-4" /> Mark complete</button>
                )}
                {perms.generate_reports && delivered && (c.org_email || c.data?.personal?.email || c.report_recipients?.dow?.length) && (
                  <EmailReport c={c} busy={busy === "email"} send={(body) => act("email", () => api.post(`/admin/cases/${id}/email-report`, body), "Secure report link emailed")} />
                )}
                {perms.generate_reports && delivered && c.report_email && !c.report_email.revoked && (
                  <button className="btn-secondary btn-sm" disabled={busy === "revoke"} data-testid="case-revoke-report-link-button" onClick={() => act("revoke", () => api.post(`/admin/cases/${id}/revoke-report-link`), "Report link turned off")}>
                    <Ban className="h-4 w-4" /> Turn off link
                  </button>
                )}
                {delivered && !c.org_email && !c.report_recipients?.is_dow && (
                  <p className="flex w-full items-center gap-2 rounded-xl bg-[#FFF1CC] px-4 py-3 text-sm font-semibold text-[#9A5B00]" data-testid="case-no-org-email-warning"><AlertTriangle className="h-4 w-4 shrink-0" /> No org email, report not sent. Add a contact email to the organization to email it.</p>
                )}
                {c.report_email && (
                  <p className="w-full text-sm text-[#1A2536]" data-testid="case-report-email-status">
                    Case {c.case_ref}: link emailed to {c.report_email.to} on {fmtDateTime(c.report_email.sent_at)} by {c.report_email.sent_by}. {c.report_email.revoked ? "Link turned off." : new Date(c.report_email.expires_at) < new Date() ? "Link expired." : `Works until ${fmtDateTime(c.report_email.expires_at)}.`}
                  </p>
                )}
                <p className="w-full text-sm text-[#64748B]" data-testid="case-report-hint">
                  {delivered ? `Marked complete ${fmtDateTime(c.delivered_at)} by ${c.delivered_by}.` : !allDone ? `Complete all ${required.length} required searches to enable the report.` : c.report ? `Generated ${fmtDateTime(c.report.generated_at)} by ${c.report.generated_by}.` : "All required searches are complete."}
                  {!perms.generate_reports && " Report generation requires additional permission."}
                </p>
              </div>
            </Card>
          )}
          {tab === "report" && submitted && (
            <Card title="Signed records" testId="case-signed-records-card">
              <div className="flex flex-wrap items-center gap-3 p-6">
                <p className="w-full text-sm text-[#64748B]">Signed forms on file, for screening partners, state submissions and charge disputes.</p>
                {c.payment_auth && (
                  <button className="btn-secondary btn-sm" data-testid="case-card-authorization-button" title="Signed credit card authorization, for charge disputes" onClick={() => downloadFile(`/admin/cases/${id}/payment-authorization.pdf`, `card-authorization-${c.case_ref}.pdf`).catch((e) => toast.error(errMsg(e)))}>
                    <FileText className="h-4 w-4" /> Card authorization
                  </button>
                )}
                {c.payment_auth && (
                  <button className="btn-secondary btn-sm" data-testid="case-dispute-packet-button" title="One PDF with the card authorization, charge receipt, service delivered, email log and case activity" onClick={() => downloadFile(`/admin/cases/${id}/dispute-packet.pdf`, `dispute-packet-${c.case_ref}.pdf`).catch((e) => toast.error(errMsg(e)))}>
                    <ShieldCheck className="h-4 w-4" /> Dispute packet
                  </button>
                )}
                <button className="btn-secondary btn-sm" data-testid="case-signed-disclosure-button" title="Disclosure and authorization with the applicant's signature, for 3rd-party screening partners" onClick={() => downloadFile(`/admin/cases/${id}/signed-disclosure.pdf`, `Signed-Disclosure-${(c.data?.personal?.last_name || "")}-${(c.data?.personal?.first_name || "")}.pdf`.replace(/[^A-Za-z0-9.-]+/g, "-")).catch((e) => toast.error(errMsg(e)))}>
                  <FileText className="h-4 w-4" /> Signed disclosure
                </button>
                {c.status === "submitted" && <StatePackageButton c={c} />}
              </div>
            </Card>
          )}

          {tab === "notes" && <>
          <Card title="Notes & internal files" testId="case-notes-card">
            <div className="p-5">
              <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); act("note", async () => { await api.post(`/admin/cases/${id}/notes`, { note }); setNote(""); }, "Note added"); }}>
                <input className="field-input h-11 flex-1 text-sm" placeholder="Add an internal note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="case-note-input" />
                <button className="btn-primary btn-sm h-11" disabled={!note.trim()} data-testid="case-note-add-button"><StickyNote className="h-4 w-4" /> Add</button>
              </form>
              <ul className="mt-4 space-y-3">
                {[...(c.notes || [])].reverse().map((n: any) => (
                  <li key={n.id} className="rounded-xl bg-[#F8F7F3] px-4 py-3 text-sm"><p className="text-[#1A2536]">{n.text}</p><p className="mt-1 text-xs text-[#94A3B8]">{n.by} · {fmtDateTime(n.at)}</p></li>
                ))}
              </ul>
              <InternalFiles c={c} onChanged={load} />
            </div>
          </Card>

          {perms.billing && <BillingSummary c={c} />}
          <ExtraFees c={c} onChanged={load} />
          </>}

          {tab === "activity" && <Card title="Activity" testId="case-timeline">
            <ul className="max-h-[70vh] divide-y divide-slate-100 overflow-y-auto">
              {(c.timeline || []).map((t: any) => (
                <li key={t.id} className="flex justify-between gap-4 px-6 py-2.5 text-xs"><span><span className="font-mono font-semibold text-[#1B365D]">{t.action}</span>{t.action?.startsWith("case.extra_fee") && t.details && <span className="ml-2 text-[#475569]" data-testid={`timeline-fee-${t.id}`}>{t.details.to ? `${t.details.from.description} $${Number(t.details.from.amount).toFixed(2)} → ${t.details.to.description} $${Number(t.details.to.amount).toFixed(2)}` : `${t.details.description} $${Number(t.details.amount).toFixed(2)}`}</span>}</span><span className="text-[#94A3B8]">{t.actor_email} · {fmtDateTime(t.at)}</span></li>
              ))}
            </ul>
          </Card>}
        </div>
      </div>
      <ConfirmDialog open={confirmClear} onOpenChange={setConfirmClear} testId="case-mark-clear-dialog" title={`Mark ${remaining} remaining search${remaining === 1 ? "" : "es"} No Record Found?`}
        description="Every required search that is not complete yet will be set to No Record Found and marked complete. Searches you already finished are not changed." confirmLabel="Mark No Record Found"
        onConfirm={async () => { await act("clear", () => api.post(`/admin/cases/${id}/searches/mark-remaining-clear`), "Remaining searches marked No Record Found"); }} />
      <ConfirmDialog open={confirmDeliver} onOpenChange={setConfirmDeliver} testId="case-deliver-dialog" title="Mark this case as complete?" description="The case and its checklist will be locked. You can then send the applicant a copy of their report." confirmLabel="Mark complete"
        onConfirm={async () => { await act("deliver", () => api.post(`/admin/cases/${id}/deliver`), "Case marked complete"); }} />
    </div>
  );
}
